import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";

// Server-side admin gate. Runs on every /admin/* navigation.
// Authoritative check uses public.has_role (user_roles table) — there is no
// client-side allowlist or metadata-based admin grant anywhere in the app.
//
// Robustness: a single transient RPC failure must NOT bounce a legitimate
// admin back to /dashboard, because /dashboard would re-redirect to /admin
// and the user gets stuck in a loading ping-pong. We retry once with a short
// delay, and only redirect away when the RPC explicitly returns `false`.
export const Route = createFileRoute("/_authenticated/admin")({
  ssr: false,
  beforeLoad: async () => {
    const callOnce = async () => {
      try {
        const { data, error } = await supabase.rpc("is_current_user_admin");
        if (error) return { ok: false as const, isAdmin: null };
        return { ok: true as const, isAdmin: Boolean(data) };
      } catch {
        return { ok: false as const, isAdmin: null };
      }
    };

    let result = await callOnce();
    if (!result.ok) {
      await new Promise((r) => setTimeout(r, 400));
      result = await callOnce();
    }

    // Only redirect away when we have a definitive non-admin answer.
    // Transient errors fall through and render the admin shell; the inner
    // page queries will surface a clearer error if the user truly lacks
    // privileges, instead of a redirect loop with /dashboard.
    if (result.ok && result.isAdmin === false) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: () => <Outlet />,
});
